import React, { useState } from "react";

import i18next from "i18next";
import { toast } from "react-toastify";
import Popup from "reactjs-popup";

import {
  useGetDeliveryDateCutOffRules,
  useDeleteDeliveryDateCutOffRule,
  useAddDeliveryDateCutOffRule,
  useEditDeliveryDateCutOffRule,
} from "api/reactQuery/queryComponents";
import { BSCoreServicesDtoMarketplaceDeliveryDateCutOffDto } from "api/reactQuery/querySchemas";
import Button from "components/Button";
import FilterableTableContainer from "components/Helpers/FilterableTableContainer";
import ModalContent from "components/Modal/ModalContent";
import { AddIcon, EditIcon, DeleteIcon } from "svg";

import RuleModal from "./RuleModal";

export type CutOffRule = BSCoreServicesDtoMarketplaceDeliveryDateCutOffDto;

const CalendarManagement: React.FC = () => {
  const {
    data: cutOffRules,
    isLoading: cutOffRulesLoading,
    refetch: refetchCutOffRules,
  } = useGetDeliveryDateCutOffRules<CutOffRule[]>({});

  const [selectedCutOffRule, setSelectedCutOffRule] = useState<number>(null);
  const [isNewRuleModalOpen, setIsNewRuleModalOpen] = useState(false);

  const { mutate: createCutOffRule } = useAddDeliveryDateCutOffRule({
    onSuccess: () => {
      refetchCutOffRules();
      setIsNewRuleModalOpen(false);
      toast.success(i18next.t("cutoff_rule_created"));
    },
  });

  const { mutate: editCutOffRule } = useEditDeliveryDateCutOffRule({
    onSuccess: () => {
      refetchCutOffRules();
      setIsNewRuleModalOpen(false);
      toast.success(i18next.t("cutoff_rule_updated"));
    },
  });

  const { mutate: deleteCutOffRule } = useDeleteDeliveryDateCutOffRule({
    onSuccess: () => {
      refetchCutOffRules();
      toast.success(i18next.t("cutoff_rule_deleted"));
    },
  });

  const handleCreateCutOffRule = (cutOffRule: CutOffRule) => {
    createCutOffRule({ body: cutOffRule });
  };

  const handleEditCutOffRule = (cutOffRule: CutOffRule) => {
    editCutOffRule({ body: cutOffRule });
  };

  const handleDeleteCutOffRule = (id: number) => {
    deleteCutOffRule({ queryParams: { id } });
  };

  const tableFields = [
    {
      displayName: i18next.t("current_date"),
      inputFilterable: true,
      name: "currentDate",
      render: ({ value, record }) =>
        record.isAnyDate ? i18next.t("any_date") : value,
      sortable: true,
    },
    {
      displayName: i18next.t("cutoff_time"),
      inputFilterable: true,
      name: "timeOfDay",
      render: ({ value, record }) =>
        record.isWholeDay ? i18next.t("whole_day") : value,
      sortable: true,
    },
    {
      displayName: i18next.t("locked_delivery_date"),
      inputFilterable: true,
      name: "lockedDeliveryDate",
      sortable: true,
    },
    {
      displayName: i18next.t("actions"),
      name: "actions",
      render: ({ value, record }) => (
        <>
          <Button
            icon={EditIcon}
            variant="outlined"
            onClick={() => {
              setSelectedCutOffRule(record.id);
              setIsNewRuleModalOpen(true);
            }}
          />
          <Popup
            modal
            trigger={
              <Button icon={DeleteIcon} color="danger" variant="outlined" />
            }
          >
            {(close) => (
              <ModalContent
                close={close}
                type="confirmDeletionModal"
                confirmDelete={() => handleDeleteCutOffRule(record.id)}
              />
            )}
          </Popup>
        </>
      ),
    },
  ];

  const tableProps = {
    data: cutOffRules || [],
    fields: tableFields,
    initialSort: "currentDate",
    namespace: "CalendarManagement",
    tableName: i18next.t("calendar_management"),
  };

  const handleSave = (cutOffRule: CutOffRule) => {
    if (cutOffRule.id) {
      handleEditCutOffRule(cutOffRule);
    } else {
      handleCreateCutOffRule(cutOffRule);
    }
  };

  return (
    <>
      <FilterableTableContainer
        {...{ isLoading: cutOffRulesLoading }}
        tableProps={tableProps}
        hasActions={true}
        buttons={
          <Button
            size="large"
            icon={AddIcon}
            onClick={() => {
              setSelectedCutOffRule(null);
              setIsNewRuleModalOpen(true);
            }}
          >
            {i18next.t("new_rule")}
          </Button>
        }
      />

      <RuleModal
        isOpen={isNewRuleModalOpen}
        selectedCutOffRule={cutOffRules?.find(
          (rule) => rule.id === selectedCutOffRule
        )}
        onSave={handleSave}
        onClose={() => setIsNewRuleModalOpen(false)}
      />
    </>
  );
};

export default CalendarManagement;
